import React, {useContext, useEffect, useState} from 'react';
import {useLocation, useParams, Navigate} from 'react-router-dom';
import {CreateOrderResponseInterface} from '../../interfaces/checkout/CreateOrderResponseInterface';
import {getWordPositionEnding} from '../../utils/Utils';
import Money from "../core/Money";
import MoneyInterface from "../../interfaces/core/MoneyInterface";
import {GlobalContext, ReducerActionType} from "../../GlobalReducer";
import {ReducerStateInterface} from "../../interfaces/core/ReducerStateInterface";

type SortKey = 'name' | 'code' | 'articul' | 'brand_name' | 'price' | 'cnt';

interface OrderTotals {
    weight: number;
    volume: number;
    count: number;
    price: MoneyInterface;
}

const VISIBLE_POSITIONS = 10;

export default function OrderSuccess() {
    const location = useLocation();
    const {orderId} = useParams<{ orderId: string }>();

    const [sortBy, setSortBy] = useState<SortKey>('name');
    const [showExtraPositions, setShowExtraPositions] = useState(false);

    const orderResponse = location.state?.orderResponse as CreateOrderResponseInterface;

    const [state, dispatch] = useContext<[ReducerStateInterface, Function]>(GlobalContext);

    useEffect(() => {
        dispatch({
            type: ReducerActionType.SET_BREADCRUMBS,
            payload: [{name: 'Заказ №' + orderResponse.order?.id + ' оформлен'}]
        })
    }, [orderResponse.order?.id]);


    if (!orderResponse || !orderId) {
        return <Navigate to="/basket" replace/>;
    }

    const positions = orderResponse.order.positions || [];

    const sortedPositions = [...positions].sort((a, b) => {
        let aValue: string | number;
        let bValue: string | number;

        if (sortBy === 'price') {
            aValue = a.price.amount;
            bValue = b.price.amount;
        } else if (sortBy === 'cnt') {
            aValue = a.cnt;
            bValue = b.cnt;
        } else {
            aValue = a.product[sortBy as keyof typeof a.product] as string | number;
            bValue = b.product[sortBy as keyof typeof b.product] as string | number;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return aValue.localeCompare(bValue);
        }
        return Number(aValue) - Number(bValue);
    });

    const visiblePositions = showExtraPositions ? sortedPositions : sortedPositions.slice(0, VISIBLE_POSITIONS);

    const totals: OrderTotals = positions.reduce((acc, position) => ({
            weight: acc.weight + (position.product.weight * position.cnt),
            volume: acc.volume + (position.product.volume * position.cnt),
            count: acc.count + position.cnt,
            price: {
                amount: acc.price.amount + (position.price.amount * position.cnt),
                currency: {name: 'RUB'}
            }
        }
    ), {weight: 0, volume: 0, count: 0, price: {amount: 0, currency: {name: 'RUB'}}});

    const handleSort = (key: SortKey) => {
        setSortBy(key);
    };

    return (
        <div className="container mt-2">
            <div>
                <h1 className={'pb-4'}>Ваш заказ {orderResponse.order.id} успешно создан!</h1>
                <p>Мы успешно создали Ваш заказ. В ближайшее время с Вами свяжется менеджер для обсуждения деталей.</p>

                <hr/>

                <div className="table-responsive">
                    <table className="table">
                        <thead>
                        <tr>
                            <th>
                                <span className={'pointer text-underline-dashed text-nowrap '}
                                      onClick={(e) => {
                                          e.preventDefault();
                                          handleSort('name');
                                      }}
                                >
                                    Название
                                </span>
                            </th>
                            <th className="text-center">
                                <span className={'pointer text-underline-dashed text-nowrap'}
                                      onClick={(e) => {
                                          e.preventDefault();
                                          handleSort('code');
                                      }}
                                >
                                    Код
                                </span>
                            </th>
                            <th className="text-center">
                                <span className={'pointer text-underline-dashed text-nowrap'}
                                      onClick={(e) => {
                                          e.preventDefault();
                                          handleSort('articul');
                                      }}
                                >
                                    Артикул
                                </span>
                            </th>
                            <th className="text-center">
                                <span className={'pointer text-underline-dashed text-nowrap'}
                                      onClick={(e) => {
                                          e.preventDefault();
                                          handleSort('brand_name');
                                      }}
                                >
                                    Бренд
                                </span>
                            </th>
                            <th className="text-right">
                                <span className={'pointer text-underline-dashed text-nowrap'}
                                      onClick={(e) => {
                                          e.preventDefault();
                                          handleSort('price');
                                      }}
                                >
                                    Цена
                                </span>
                            </th>
                            <th className="text-right">
                                <span className={'text-nowrap'}>Вес/Объем(ед.)</span>
                            </th>
                            <th className="text-center">
                                    <span>
                                        <span className={'pointer text-underline-dashed text-nowrap'}
                                              onClick={(e) => {
                                                  e.preventDefault();
                                                  handleSort('cnt');
                                              }}
                                        >
                                            Кол-во
                                        </span>
                                    </span>
                            </th>
                            <th className="text-right">Стоимость</th>
                        </tr>
                        </thead>
                        <tbody>
                        {visiblePositions.map((position) => (
                            position.product.code && (
                                <tr key={position.id} className={position.is_new ? 'text-decoration-line-through' : ''}>
                                    <td>
                                        <span>{position.product.name}</span>
                                        &nbsp;&nbsp;&nbsp;
                                        <small>
                                            <a
                                                href={`#${position.product.brand_name}/${position.product.code}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                    <span>
                                                        <i className="fa fa-tags"></i> смотреть на сайте
                                                    </span>
                                            </a>
                                        </small>
                                    </td>
                                    <td className="text-center">
                                        <span>{position.product.code}</span>
                                    </td>
                                    <td className="text-center">
                                        <span>{position.product.articul}</span>
                                    </td>
                                    <td className="text-center">
                                        <span>{position.product.brand_name}</span>
                                    </td>
                                    <td className="text-right">
                                            <span className={'text-nowrap'}> <Money value={position.price}/> </span>
                                            {(position.price_from_basket?.amount || 0) > 0 && (position.price_from_basket?.amount || 0) != position.price.amount && <div className={'text-nowrap text-danger text-decoration-line-through'}> <Money value={position.price_from_basket as MoneyInterface}/> </div>}
                                    </td>
                                    <td className="text-right">
                                            <span>
                                                <span>{position.product.weight}</span> кг. <br/>
                                                <small
                                                    className="text-muted">{position.product.volume}</small> м<sup>3</sup>
                                            </span>
                                    </td>
                                    <td className="text-center">
                                            <span title={`${position.cnt} шт.`}>
                                                <span>{position.cnt}</span> шт.
                                                {(position.cnt_from_basket || 0) > 0 && (position.cnt_from_basket || 0) != position.cnt && <div className={'text-decoration-line-through text-danger'}>{position.cnt_from_basket}  шт.</div>}
                                            </span>
                                        {position.is_new && (
                                            <sup className="label label-danger font-size-80">нов.</sup>
                                        )}
                                    </td>
                                    <td className="text-right">
                                            <span className={'text-nowrap'}>
                                                <Money value={{
                                                    amount: position.price.amount * position.cnt,
                                                    currency: {name: 'RUB'}
                                                }}/>
                                            </span>
                                    </td>
                                </tr>
                            )
                        ))}

                        {positions.length > VISIBLE_POSITIONS && (
                            <tr>
                                <td colSpan={8} className="text-center py-4">
                                        <span className={'pointer text-underline-dashed text-danger '}
                                              onClick={() => setShowExtraPositions(!showExtraPositions)}
                                        >
                                            {showExtraPositions ? ' скрыть ' : ' показать еще '}
                                            <span>{positions.length - VISIBLE_POSITIONS}</span>
                                            <span> {getWordPositionEnding(positions.length - VISIBLE_POSITIONS)}</span>
                                        </span>
                                </td>
                            </tr>
                        )}

                        <tr>
                            <th colSpan={5}>ИТОГО:</th>
                            <th className="text-right">
                                    <span>
                                        <span>{totals.weight.toFixed(2)}</span> кг. <br/>
                                        <small className="text-muted">{totals.volume.toFixed(3)}</small> м<sup>3</sup>
                                    </span>
                            </th>
                            <th className="text-right">
                                <span>{totals.count}</span> шт.
                            </th>
                            <th className="text-right">
                                    <span>
                                        <Money value={totals.price}/>
                                    </span>
                            </th>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}